/**
 * =============================================================================
 * Componente: CampaignTermsDialog
 * Versão: 2.0.0 (2026-05-01)
 *
 * Descrição:
 *   Modal acionado pelo botão "Regras" no card de campanha (portal do
 *   afiliado). Carrega o modelo de contrato vinculado via RPC e exibe
 *   uma pré-visualização COMPLETA do PDF (capa + conteúdo + cláusulas
 *   fixas) reaproveitando ContractFullPreview do módulo CRM.
 *
 *   Responsividade:
 *     - Fullscreen real no mobile (100vw x 100dvh, sem padding lateral).
 *     - 95vw / 85vh em ≥ sm (idêntico ao padrão de modais do projeto).
 *     - O preview interno usa scale dinâmico para a página A4 caber
 *       em qualquer largura sem scroll horizontal.
 *
 * Releases:
 *   - 2.0.0 (2026-05-01) Substitui ContractTemplatePreview por
 *     ContractFullPreview e adiciona fullscreen mobile.
 *   - 1.0.0 (2026-05-01) Versão inicial.
 * =============================================================================
 */
import { Loader2, FileText, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ContractFullPreview } from '@/components/contracts/ContractFullPreview';
import { useAffiliateCampaignTerms } from '@/hooks/useAffiliateCampaignTerms';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: string | null;
  campaignName?: string | null;
}

export function CampaignTermsDialog({
  open,
  onOpenChange,
  campaignId,
  campaignName,
}: Props) {
  const { data, isLoading, isError, error } = useAffiliateCampaignTerms(
    campaignId,
    open,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Mobile: fullscreen sem bordas (100vw x 100dvh, max-w-none).
        ≥ sm: largura confortável centralizada (max-w-4xl, 85vh).
      */}
      <DialogContent
        className="
          p-0 gap-0 flex flex-col overflow-hidden
          w-screen h-[100dvh] max-w-none rounded-none
          sm:w-[95vw] sm:h-[85vh] sm:max-w-4xl sm:rounded-lg
        "
      >
        <DialogHeader className="px-3 sm:px-4 py-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-sm sm:text-base">
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="shrink-0">Regras da Campanha</span>
            {campaignName && (
              <span className="text-muted-foreground font-normal truncate min-w-0">
                — {campaignName}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden p-2 sm:p-4">
          {isLoading && (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Carregando regras...
            </div>
          )}

          {isError && !isLoading && (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
              <p className="text-sm text-muted-foreground">
                {(error as Error | null)?.message ||
                  'Não foi possível carregar as regras desta campanha.'}
              </p>
            </div>
          )}

          {!isLoading && !isError && data && (
            <ContractFullPreview
              contentHtml={data.content_html}
              backgroundImagePath={data.background_image_path}
              coverPdfPath={data.cover_pdf_path}
              basePdfPath={data.base_pdf_path}
              basePdfPosition={data.base_pdf_position}
              showPageNumbers={data.show_page_numbers}
              pageNumberPosition={data.page_number_position ?? 'bottom-right'}
              bgMarginTopMm={data.bg_margin_top_mm ?? undefined}
              bgMarginBottomMm={data.bg_margin_bottom_mm ?? undefined}
              paginationYOffsetMm={data.pagination_y_offset_mm ?? undefined}
              className="h-full"
            />
          )}

          {!isLoading && !isError && !data && (
            <div className="text-center py-12 text-sm text-muted-foreground">
              Nenhuma regra cadastrada para esta campanha.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
